export const CRM_THEME_STORAGE_KEY = "crm_theme";
export const CRM_LEGACY_THEME_STORAGE_KEY = "td_theme";

const VALID_THEMES = new Set(["light", "dark"]);

export function normalizeThemeMode(value, fallback = "light") {
  if (value === true) return "dark";
  if (value === false) return "light";

  const normalized = String(value ?? "").trim().toLowerCase();
  return VALID_THEMES.has(normalized) ? normalized : fallback;
}

function browserStorage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function prefersDarkMode() {
  try {
    return Boolean(window.matchMedia?.("(prefers-color-scheme: dark)")?.matches);
  } catch {
    return false;
  }
}

/**
 * Resolves one product-wide visual mode. The older ticket-detail key remains
 * a read-through migration source until those legacy screens are fully moved
 * onto the shared design system.
 */
export function readThemePreference({ storage = browserStorage(), systemDark = prefersDarkMode() } = {}) {
  try {
    const canonical = storage?.getItem(CRM_THEME_STORAGE_KEY);
    if (VALID_THEMES.has(canonical)) return canonical;

    const legacy = storage?.getItem(CRM_LEGACY_THEME_STORAGE_KEY);
    if (VALID_THEMES.has(legacy)) return legacy;
  } catch {
    // A restricted storage policy falls through to the operating-system mode.
  }

  return systemDark ? "dark" : "light";
}

/**
 * The App controller is the only runtime writer. DOM attributes are updated
 * together so native CSS and third-party portals never disagree about mode.
 */
export function applyThemePreference(
  value,
  { storage = browserStorage(), documentRef = typeof document === "undefined" ? null : document } = {}
) {
  const mode = normalizeThemeMode(value);

  documentRef?.documentElement?.setAttribute("data-theme", mode);
  documentRef?.body?.setAttribute("data-theme", mode);

  try {
    if (storage?.getItem(CRM_THEME_STORAGE_KEY) !== mode) {
      storage?.setItem(CRM_THEME_STORAGE_KEY, mode);
    }
    // Compatibility bridge for the two legacy email-ticket screens. This is
    // intentionally written here, never by shell children.
    if (storage?.getItem(CRM_LEGACY_THEME_STORAGE_KEY) !== mode) {
      storage?.setItem(CRM_LEGACY_THEME_STORAGE_KEY, mode);
    }
  } catch {
    // The in-memory mode and DOM theme remain usable without persistence.
  }

  return mode;
}

export function initializeThemePreference(options) {
  const mode = readThemePreference(options);
  return applyThemePreference(mode, options);
}

export function themeFromStorageEvent(event, currentMode = "light") {
  if (event?.key === CRM_THEME_STORAGE_KEY) {
    return normalizeThemeMode(event.newValue, currentMode);
  }
  if (event?.key === CRM_LEGACY_THEME_STORAGE_KEY) {
    return normalizeThemeMode(event.newValue, currentMode);
  }
  return currentMode;
}
